import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { add } from 'ionicons/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMasters } from '../../lib/data/queries.js';
import { FoodEditorModal } from '../../components/editors/FoodEditorModal.js';
import { FeatureIntroBlock, isIntroDismissed } from '../../components/FeatureCard.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { canCreateFood } from '../../lib/auth/capabilities.js';

interface DetergentFood {
  id: string;
  name: string;
  category?: string | null;
  storage_type?: string | null;
  current_batch_lot?: string | null;
  archived_at?: string | null;
}

export function CleaningProductsPage(): JSX.Element {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const canCreateFoods = canCreateFood(profile);
  // T-159: la fonte UNICA dei prodotti di pulizia è `foods` con categoria
  // "detersivi". Creare un prodotto = creare un alimento (FoodEditorModal
  // detergentMode) → popola il magazzino, niente più un secondo database.
  // I record precedenti vengono migrati su foods mantenendo lo stesso UUID.
  const { data: allFoods = [] } = useMasters<DetergentFood>('foods');
  const detergents = allFoods.filter((f) => f.category === 'detersivi' && !f.archived_at);

  const [toast, setToast] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  // T-077: forziamo un re-render quando l'utente clicca "Inizia subito".
  const [introVersion, setIntroVersion] = useState(0);
  const featureKey = 'cleaning_products';
  const userId = profile?.id ?? null;
  const businessId = profile?.active_business_id ?? null;
  const showIntro = detergents.length === 0
    && !isIntroDismissed(featureKey, userId, businessId);
  void introVersion;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/haccp/cleaning" />
          </IonButtons>
          <IonTitle>Prodotti</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 96px' }}>
          {showIntro ? (
            <FeatureIntroBlock
              featureKey={featureKey}
              icon="🧴"
              title="Prodotti"
              description={
                <>
                  Anagrafa i prodotti per la pulizia e sanificazione che usi nel
                  locale (detergenti, disinfettanti, sgrassatori, brillantante,
                  ecc.). Si creano <b>come gli alimenti</b> e finiscono nel
                  magazzino: un solo elenco, niente doppia anagrafica.
                  <br /><br />
                  Avere l'anagrafica completa serve a dimostrare in caso di
                  ispezione ASL che i prodotti usati sono idonei, e per
                  collegarli ai piani di pulizia.
                </>
              }
              whatsappQuestion="Ciao, ho dubbi su come anagrafare i prodotti di pulizia in TrackEAT, mi puoi aiutare?"
              startLabel="Aggiungi il primo prodotto"
              onStart={() => { setIntroVersion((v) => v + 1); setEditorOpen(true); }}
              userId={userId}
              businessId={businessId}
              flush
            />
          ) : (
            <>
              {detergents.length === 0 ? (
                <div style={{ background: '#fff', padding: 16, borderRadius: 14, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
                  {t('common.empty')}
                </div>
              ) : (
                detergents.map((f) => (
                  <div
                    key={f.id}
                    style={{
                      background: '#fff',
                      borderRadius: 14,
                      borderLeft: '4px solid #0d9488',
                      boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                      margin: '10px 0',
                      padding: '12px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: '#ccfbf1', color: '#0d9488',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, flexShrink: 0,
                    }}>
                      🧴
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: '#0f172a' }}>{f.name}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        Magazzino · detersivi
                        {f.current_batch_lot && f.current_batch_lot !== '-' ? ` · lotto ${f.current_batch_lot}` : ''}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>

        {canCreateFoods && <IonFab slot="fixed" vertical="bottom" horizontal="end">
          <IonFabButton aria-label="Nuovo prodotto" onClick={() => setEditorOpen(true)}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>}

        <FoodEditorModal
          isOpen={editorOpen && canCreateFoods}
          detergentMode
          onDismiss={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); setToast('Prodotto creato'); }}
        />

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={1500}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
